import { ApplicationConfig, provideZoneChangeDetection } from "@angular/core";
import { provideRouter, RouteReuseStrategy, withComponentInputBinding } from "@angular/router";
import { provideHttpClient, withFetch, withInterceptors } from "@angular/common/http";
import { authInterceptor } from "./interceptors/auth.interceptor";
import { PaymentAwareToastService, paymentMethodRequiredInterceptor } from "@zaeper/angular-dashboard-view-lib";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideLottieOptions } from "ngx-lottie";
import { providePrimeNG } from "primeng/config";
import { BloxPreset } from "@zaeper/primeng-themes";

import { DictionaryService, ILanguageStateService, LanguageStateService } from "@zaeper/localization-lib";
import { dictionaryServiceInjectionToken } from "@zaeper/angular-localization-lib";
import {
  BlockingLoaderService,
  blockingLoaderServiceInjectionToken,
  BlockingLoaderStateService,
  blockingLoaderStateServiceInjectionToken,
  ConfirmationModalService,
  confirmationModalServiceInjectionToken,
  ModalService,
  modalServiceInjectionToken,
  ThemeService,
  themeServiceInjectionToken,
  ToastService,
  toastServiceInjectionToken,
  ToastStateService,
  toastStateServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { DialogService } from "primeng/dynamicdialog";
import { storeStateServiceInjectionToken } from "@zaeper/angular-application-store-lib";

import { routes } from "./routing/app.routes";
import { AppRouteReuseStrategy } from "./routing/app-route-reuse-strategy";

// Language services
import { languageStateServiceInjectionToken } from "./services/language/languageStateServiceInjectionToken";
import { languageResolverServiceInjectionToken } from "./services/language/languageResolverServiceInjectionToken";
import { LanguageResolverService } from "./services/language/languageResolver.service";

// Dictionary services
import { dictionaryResolverServiceInjectionToken } from "./services/dictionary/dictionary-resolver-service-injection-token";
import { DictionaryResolverService } from "./services/dictionary/dictionaryResolver.service";

// Global state services
import { sessionStateServiceInjectionToken } from "./global-states/session/session-state-service-injection-token";
import { SessionStateService } from "./global-states/session/session-state.service";
import { StoreStateService } from "./global-states/store/store-state.service";

// Infrastructure services
import { infrastructureServiceInjectionToken } from "./services/infrastructure/infrastructure-service-injection-token";
import { InfrastructureService } from "./services/infrastructure/infrastructure.service";
import { livekitClientInjectionToken } from "./services/livekit/livekit-client-injection-token";
import { LivekitClient } from "./services/livekit/livekit.client";

// REST clients
import {
  userAuthenticationRestClientInjectionToken
} from "./rest-clients/user/authentication/user-authentication-rest-client-injection-token";
import { UserAuthenticationRestClient } from "./rest-clients/user/authentication/user-authentication-rest.client";
import { twoFactorRestClientInjectionToken } from "@zaeper/angular-2fa-lib";
import { TwoFactorRestClient } from "./rest-clients/two-factor/two-factor-rest.client";
import { cloudRestClientInjectionToken } from "./rest-clients/cloud/cloud-rest-client-injection-token";
import { CloudRestClient } from "./rest-clients/cloud/cloud-rest.client";
import { groupRestClientInjectionToken } from "./rest-clients/group/group-rest-client-injection-token";
import { GroupRestClient } from "./rest-clients/group/group-rest.client";
import { receiverRestClientInjectionToken } from "./rest-clients/receiver/receiver-rest-client-injection-token";
import { ReceiverRestClient } from "./rest-clients/receiver/receiver-rest.client";
import {
  appRestClientInjectionToken,
  appSettingsRestClientInjectionToken,
  launcherRestClientInjectionToken,
  launcherSettingsRestClientInjectionToken,
  widgetRestClientInjectionToken,
  widgetSettingsRestClientInjectionToken
} from "@zaeper/angular-application-store-lib";
import { AppRestClient } from "./rest-clients/application/app/app-rest.client";
import { AppSettingsRestClient } from "./rest-clients/application/app/app-settings-rest.client";
import { LauncherRestClient } from "./rest-clients/application/launcher/launcher-rest.client";
import { LauncherSettingsRestClient } from "./rest-clients/application/launcher/launcher-settings-rest.client";
import { WidgetRestClient } from "./rest-clients/application/widget/widget-rest.client";
import { WidgetSettingsRestClient } from "./rest-clients/application/widget/widget-settings-rest.client";
import { companyRestClientInjectionToken } from "./rest-clients/company/company-rest-client-injection-token";
import { CompanyRestClient } from "./rest-clients/company/company-rest.client";
import { licenseRestClientInjectionToken } from "./rest-clients/license/license-rest-client-injection-token";
import { LicenseRestClient } from "./rest-clients/license/license-rest.client";
import { billingRestClientInjectionToken } from "./rest-clients/billing/billing-rest-client-injection-token";
import { BillingRestClient } from "./rest-clients/billing/billing-rest.client";

// WS clients
import { receiverWsClientInjectionToken } from "./ws-clients/receiver/receiver-ws-client-injection-token";
import { ReceiverWsClient } from "./ws-clients/receiver/receiver-ws.client";
import {
  receiverAuthenticationWsClientInjectionToken
} from "./ws-clients/receiver-authentication/receiver-authentication-ws-client-injection-token";
import { ReceiverAuthenticationWsClient } from "./ws-clients/receiver-authentication/receiver-authentication-ws.client";

// Guards
import { authenticationGuardInjectionToken } from "./routing/guards/session/authentication-guard-injection-token";
import { AuthenticationGuard } from "./routing/guards/session/authentication-guard.service";

// Resolvers
import { currentUserResolverInjectionToken } from "./routing/resolvers/session/current-user-resolver-injection-token";
import { CurrentUserResolver } from "./routing/resolvers/session/current-user.resolver";
import {
  quickAccessResourcesResolverInjectionToken
} from "./routing/resolvers/session/quick-access-resources-resolver-injection-token";
import { QuickAccessResourcesResolver } from "./routing/resolvers/session/quick-access-resources-resolver.service";

// Stripe
import { stripeServiceInjectionToken } from "./services/stripe/stripe-service-injection-token";
import { StripeService } from "./services/stripe/stripe.service";

// Splash loader
import { splashLoaderStateServiceInjectionToken } from "./components/splash-loader/splash-loader-state-service-injection-token";
import { SplashLoaderStateService } from "./components/splash-loader/splash-loader-state.service";
import { splashLoaderServiceInjectionToken } from "./components/splash-loader/splash-loader-service-injection-token";
import { SplashLoaderService } from "./components/splash-loader/splash-loader-service";

// Modals
import {
  cloudCreateUpdateModalServiceInjectionToken
} from "./modals/cloud-create-update-modal/cloud-create-update-modal-service-injection-token";
import { CloudCreateUpdateModalService } from "./modals/cloud-create-update-modal/cloud-create-update-modal.service";
import {
  groupCreateUpdateModalServiceInjectionToken
} from "./modals/group-create-update-modal/group-create-update-modal-service-injection-token";
import { GroupCreateUpdateModalService } from "./modals/group-create-update-modal/group-create-update-modal.service";
import {
  scheduleReceiverConfigPushModalServiceInjectionToken
} from "./modals/schedule-receiver-config-push-modal/schedule-receiver-config-push-modal-service-injection-token";
import {
  ScheduleReceiverConfigPushModalService
} from "./modals/schedule-receiver-config-push-modal/schedule-receiver-config-push-modal.service";
import {
  applicationsOverviewModalServiceInjectionToken
} from "./modals/applications-overview-modal/applications-overview-modal-service-injection-token";
import { ApplicationsOverviewModalService } from "./modals/applications-overview-modal/applications-overview-modal.service";
import {
  availableAppsOverviewModalServiceInjectionToken
} from "./modals/available-apps-overview-modal/available-apps-overview-modal-service-injection-token";
import { AvailableAppsOverviewModalService } from "./modals/available-apps-overview-modal/available-apps-overview-modal.service";
import {
  appSettingsModalServiceInjectionToken
} from "./modals/app-settings-modal/app-settings-modal-service-injection-token";
import { AppSettingsModalService } from "./modals/app-settings-modal/app-settings-modal.service";
import {
  connectReceiverModalServiceInjectionToken
} from "./modals/connect-receiver-modal/connect-receiver-modal-service-injection-token";
import { ConnectReceiverModalService } from "./modals/connect-receiver-modal/connect-receiver-modal.service";
import {
  qrCodeModalServiceInjectionToken
} from "./modals/qr-code-modal/qr-code-modal-service-injection-token";
import { QrCodeModalService } from "./modals/qr-code-modal/qr-code-modal.service";
import {
  fullscreenPreviewModalServiceInjectionToken
} from "./modals/fullscreen-preview-modal/fullscreen-preview-modal-service-injection-token";
import { FullscreenPreviewModalService } from "./modals/fullscreen-preview-modal/fullscreen-preview-modal.service";
import {
  switchCompanyModalServiceInjectionToken
} from "./modals/switch-company-modal/switch-company-modal-service-injection-token";
import { SwitchCompanyModalService } from "./modals/switch-company-modal/switch-company-modal.service";
import {
  welcomeWizardModalServiceInjectionToken
} from "./modals/welcome-wizard-modal/welcome-wizard-modal-service-injection-token";
import { WelcomeWizardModalService } from "./modals/welcome-wizard-modal/welcome-wizard-modal.service";
import {
  infoModalServiceInjectionToken
} from "./modals/info-modal/info-modal-service-injection-token";
import { InfoModalService } from "./modals/info-modal/info-modal.service";
import {
  addPaymentMethodModalServiceInjectionToken
} from "./modals/add-payment-method-modal/add-payment-method-modal-service-injection-token";
import { AddPaymentMethodModalService } from "./modals/add-payment-method-modal/add-payment-method-modal.service";
import { xsrfInterceptor } from "@zaeper/angular-core-lib";

const dictionaryServiceFactory = (languageStateService: ILanguageStateService) => {
  return new DictionaryService(languageStateService);
};

export function playerFactory() {
  return import("lottie-web");
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withFetch(), withInterceptors([xsrfInterceptor, authInterceptor, paymentMethodRequiredInterceptor])),
    provideAnimationsAsync(),
    provideLottieOptions( {
      player: playerFactory
    } ),
    providePrimeNG({
      theme: {
        preset: BloxPreset,
        options: {
          cssLayer: {
            name: "primeng",
            order: "base, primeng, primeicons, app-theme, components, theme, utilities"
          },
          darkModeSelector: ".app-dark"
        }
      }
    }),

    // Route reuse strategy
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },

    // Toast services
    { provide: toastServiceInjectionToken, useClass: PaymentAwareToastService },
    { provide: toastStateServiceInjectionToken, useClass: ToastStateService },

    // Language services
    { provide: languageStateServiceInjectionToken, useClass: LanguageStateService },
    { provide: languageResolverServiceInjectionToken, useClass: LanguageResolverService },

    // Dictionary services
    {
      provide: dictionaryServiceInjectionToken,
      useFactory: dictionaryServiceFactory,
      deps: [languageStateServiceInjectionToken]
    },
    { provide: dictionaryResolverServiceInjectionToken, useClass: DictionaryResolverService },

    // Global state services
    { provide: sessionStateServiceInjectionToken, useClass: SessionStateService },
    { provide: storeStateServiceInjectionToken, useClass: StoreStateService },

    // Infrastructure services
    { provide: infrastructureServiceInjectionToken, useClass: InfrastructureService },
    { provide: livekitClientInjectionToken, useClass: LivekitClient },

    // Stripe
    { provide: stripeServiceInjectionToken, useClass: StripeService },

    // REST clients
    { provide: receiverRestClientInjectionToken, useClass: ReceiverRestClient },
    { provide: userAuthenticationRestClientInjectionToken, useClass: UserAuthenticationRestClient },
    { provide: cloudRestClientInjectionToken, useClass: CloudRestClient },
    { provide: groupRestClientInjectionToken, useClass: GroupRestClient },
    { provide: appRestClientInjectionToken, useClass: AppRestClient },
    { provide: appSettingsRestClientInjectionToken, useClass: AppSettingsRestClient },
    { provide: launcherRestClientInjectionToken, useClass: LauncherRestClient },
    { provide: launcherSettingsRestClientInjectionToken, useClass: LauncherSettingsRestClient },
    { provide: widgetRestClientInjectionToken, useClass: WidgetRestClient },
    { provide: widgetSettingsRestClientInjectionToken, useClass: WidgetSettingsRestClient },
    { provide: companyRestClientInjectionToken, useClass: CompanyRestClient },
    { provide: licenseRestClientInjectionToken, useClass: LicenseRestClient },
    { provide: billingRestClientInjectionToken, useClass: BillingRestClient },
    { provide: twoFactorRestClientInjectionToken, useClass: TwoFactorRestClient },

    // WS clients
    { provide: receiverWsClientInjectionToken, useClass: ReceiverWsClient },
    { provide: receiverAuthenticationWsClientInjectionToken, useClass: ReceiverAuthenticationWsClient },

    // Guards
    { provide: authenticationGuardInjectionToken, useClass: AuthenticationGuard },

    // Resolvers
    { provide: currentUserResolverInjectionToken, useClass: CurrentUserResolver },
    { provide: quickAccessResourcesResolverInjectionToken, useClass: QuickAccessResourcesResolver },

    // Splash loader
    { provide: splashLoaderStateServiceInjectionToken, useClass: SplashLoaderStateService },
    { provide: splashLoaderServiceInjectionToken, useClass: SplashLoaderService },

    // Blocking loader
    { provide: blockingLoaderStateServiceInjectionToken, useClass: BlockingLoaderStateService },
    { provide: blockingLoaderServiceInjectionToken, useClass: BlockingLoaderService },

    // Modal services
    DialogService,
    { provide: modalServiceInjectionToken, useClass: ModalService },
    { provide: confirmationModalServiceInjectionToken, useClass: ConfirmationModalService },
    { provide: cloudCreateUpdateModalServiceInjectionToken, useClass: CloudCreateUpdateModalService },
    { provide: groupCreateUpdateModalServiceInjectionToken, useClass: GroupCreateUpdateModalService },
    { provide: scheduleReceiverConfigPushModalServiceInjectionToken, useClass: ScheduleReceiverConfigPushModalService },
    { provide: applicationsOverviewModalServiceInjectionToken, useClass: ApplicationsOverviewModalService },
    { provide: availableAppsOverviewModalServiceInjectionToken, useClass: AvailableAppsOverviewModalService },
    { provide: appSettingsModalServiceInjectionToken, useClass: AppSettingsModalService },
    { provide: connectReceiverModalServiceInjectionToken, useClass: ConnectReceiverModalService },
    { provide: qrCodeModalServiceInjectionToken, useClass: QrCodeModalService },
    { provide: fullscreenPreviewModalServiceInjectionToken, useClass: FullscreenPreviewModalService },
    { provide: switchCompanyModalServiceInjectionToken, useClass: SwitchCompanyModalService },
    { provide: welcomeWizardModalServiceInjectionToken, useClass: WelcomeWizardModalService },
    { provide: infoModalServiceInjectionToken, useClass: InfoModalService },
    { provide: addPaymentMethodModalServiceInjectionToken, useClass: AddPaymentMethodModalService },

    // Theme
    { provide: themeServiceInjectionToken, useClass: ThemeService }
  ]
};
